import { CdcaApiResponse, CadastroCdcaApiRequest, InstrumentoFinanceiro, TipoRegistroInstrumentoFinanceiro, ReservarUltimoNumeroDaEmissaoRequest } from 'common/genericTypes';
import FormularioCDCA from 'pages/cdca/registro/formulario';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { RootState } from 'store';
import {
  resetCdcaDetails,
  updateCdcaCaracteristicas,
  updateField,
  resetMetadata,
} from 'store/modules/cdca/caracteristicas';
import { resetCriteriosRemuneracao, updateCriteriosRemuneracao } from 'store/modules/cdca/criteriosRemuneracao';
import { resetEventos, updateEventos } from 'store/modules/cdca/eventos';
import { resetDocuments, updateDocuments } from 'store/modules/cdca/uploadDocumentos';
import { updateId, resetEmissor } from 'store/modules/cdca/emissor';
import { UploadDocResponse } from 'store/modules/models/uploadDocResponse';
import CdcaParser from 'utils/parsers/cdcaParser';
import { addAlert } from 'store/modules/snackBar';
import { highlightErrors, typeOfRegister } from 'store/modules/cdca';

type ComponentParams = { cdcaId?: string };
type ComponentState = { cdcaRascunho?: CdcaApiResponse; acao?: TipoRegistroInstrumentoFinanceiro };

type Props = RouteComponentProps<ComponentParams, {}, ComponentState>;

const Cdca: React.FC<Props> = (props) => {
  const { cdcaId } = props.match.params;
  const cdcaRascunho = props.location.state?.cdcaRascunho;

  const history = useHistory();
  const dispatch = useDispatch();
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);

  const caracteristicas = useSelector((state: RootState) => state.cdcaCadastro.caracteristicas);
  const criteriosRemuneracao = useSelector((state: RootState) => state.cdcaCadastro.criteriosRemuneracao);
  const eventos = useSelector((state: RootState) => state.cdcaCadastro.eventos);
  const uploadDocumentos = useSelector((state: RootState) => state.cdcaCadastro.uploadDocumentos);
  const emissor = useSelector((state: RootState) => state.cdcaCadastro.emissor);
  
  const cdcaApi = useSelector((state: RootState) => state.cdcaApi);
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);

  const [codigo, setCodigo] = useState<string>('');

  const submitPutCdca = async (
    cdcaId: string,
    cdca: CadastroCdcaApiRequest
  ) => {
    let result = { codigo: '', ticket: '' };
    try {
      const res = await cdcaApi.putCdca(dispatch, cdcaId, cdca, true);
      result = { codigo: res.codigo, ticket: res.ticket };
    } catch (error) { }
    return result;
  };

  const submitPostCdca = async (): Promise<CdcaApiResponse> => {
    try {
      const cdca: CadastroCdcaApiRequest = CdcaParser.stateToCadastroCdcaApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );
      return await cdcaApi.postCdca(dispatch, cdca, true);
    } catch (error) {
      return {} as CdcaApiResponse;
    }
  };

  const submitCdcaDocument = async (
    cdcaId: string,
    emissionType: string,
    file: File
  ): Promise<UploadDocResponse[]> => {
    try {
      return cdcaApi.putUploadDoc(dispatch, cdcaId, emissionType, file, true);
    } catch (error) {
      return [];
    }
  };

  const reservarNumeroEmissao = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return false;
    }
    if (!caracteristicas?.cnpjEmissor) {
      dispatch(addAlert({ message: 'CNPJ do emissor não encontrado', severity: 'error' }));
      return false;
    }

    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjEmissor,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.Cdca,
        externalId: participanteId,
      };

      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
      return true;
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return false;
    }
  };

  const reserveBeforeHandleSubmit = async (handleSubmit: () => Promise<{ codigo: string; ticket: string } | undefined>): Promise<{ codigo: string; ticket: string } | undefined> => {
    const numeroEmissaoReservado = await reservarNumeroEmissao();
    if (!numeroEmissaoReservado) return;
    return await handleSubmit();
  }

  const handleEditSubmit = async () => {
    if (!cdcaId) return { codigo: '', ticket: '' };

    const cdca: CadastroCdcaApiRequest = CdcaParser.stateToCadastroCdcaApiRequest(
      caracteristicas,
      criteriosRemuneracao,
      eventos,
      uploadDocumentos,
      emissor
    );

    const cdcaResponse = await submitPutCdca(cdcaId, cdca);

    for (const document of uploadDocumentos.documents) {
      if (document.file) {
        const uploadResponse = await submitCdcaDocument(cdcaId, document.type, document.file);
        if (!uploadResponse || uploadResponse.length === 0) return { codigo: '', ticket: '' };
      }
    }

    if (caracteristicas.id) {
      for (const documentToDelete of uploadDocumentos.documentsToDelete) {
        documentToDelete.remoteFileInfo?.id &&
          (await cdcaApi.deleteUploadDoc(dispatch, cdcaId, documentToDelete.remoteFileInfo.id, true));
      }
    }

    return cdcaResponse;
  };

  const handleRegisterSubmit = async () => {
    const uploadedCdca = await submitPostCdca();

    if (uploadedCdca.id) {
      for (const document of uploadDocumentos.documents) {
        if (document.file) {
          await submitCdcaDocument(uploadedCdca.cdcaId, document.type, document.file);
        }
      }
    }

    return { codigo: '', ticket: uploadedCdca.ticket };
  };

  const handleAditamentoSubmit = async (): Promise<{ codigo: string, ticket: string } | undefined> => {
    try {
      if (codigo) {
        return { codigo, ticket: '' };
      } else throw 'ID do CDCA não identificado';
    } catch (error) {
      console.error(error);
    }
  };

  const handleReturnToList = () => {
    history.push('/solicitacoes');
  };
  
  const handleSalvarRascunho = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return '';
    }
    if (!caracteristicas?.cnpjEmissor) {
      dispatch(addAlert({ message: 'CNPJ do emissor não encontrado', severity: 'error' }));
      return '';
    }
    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjEmissor,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.Cdca,
        externalId: participanteId,
      };
      
      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return '';
    }
    let draftId = '';
    try {
      const rascunho = CdcaParser.stateToCadastroCdcaApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );

      const documents = uploadDocumentos.documents
        .filter((doc) => !!doc.file)
        .map((doc) => ({ file: doc.file!, emissionType: doc.type }));

      const result = caracteristicas.draftId
        ? await instrumentoApi.putRascunho(
          dispatch,
          InstrumentoFinanceiro.Cdca,
          caracteristicas.draftId,
          rascunho,
          documents,
          true
        )
        : await instrumentoApi.postRascunho(
          dispatch,
          InstrumentoFinanceiro.Cdca,
          rascunho,
          documents,
          true
        );
      setCodigo(result.codigo);
      draftId = result.draftId;
      dispatch(updateField({ name: 'draftId', value: draftId }));

      dispatch(addAlert({ message: `Rascunho salvo com sucesso. Código do Instrumento Financeiro ${result.codigo}`, severity: 'success' }));
    } catch (error) { }
    return draftId;
  };

  const [typeOfFormulario, setTypeOfFormulario] = useState<TipoRegistroInstrumentoFinanceiro>(TipoRegistroInstrumentoFinanceiro.Cadastro);

  const defineTypeOfRegister = async (): Promise<void> => {
    if (cdcaId) {
      if (props.location.state?.acao) {
        setTypeOfFormulario(props.location.state.acao);
        dispatch(typeOfRegister(props.location.state.acao));
      }
    } else {
      setTypeOfFormulario(TipoRegistroInstrumentoFinanceiro.Cadastro);
      dispatch(typeOfRegister(TipoRegistroInstrumentoFinanceiro.Cadastro));
    }
  };

  useEffect(() => {
    dispatch(resetEmissor());
    dispatch(resetMetadata())
    dispatch(resetCdcaDetails());
    dispatch(resetCriteriosRemuneracao());
    dispatch(resetEventos());
    dispatch(resetDocuments());
    dispatch(highlightErrors(false));
    defineTypeOfRegister();
  }, []);

  useEffect(() => {
    if (cdcaId) {
      cdcaApi
        .getCdcaDetalhes(dispatch, cdcaId, true)
        .then((response) => {
          if (response) {
            setCodigo(response.codigo);
            dispatch(updateId(response.emissorId ?? ''));
            dispatch(updateCdcaCaracteristicas(CdcaParser.apiResponseToCaracteristicasState(response)));
            dispatch(updateCriteriosRemuneracao(CdcaParser.apiResponseToCriteriosRemuneracaoState(response)));
            dispatch(updateEventos(CdcaParser.apiResponseToEventosState(response)));
            dispatch(updateDocuments(CdcaParser.apiResponseToCdcaDocumentsState(response)));
          }
        })
        .catch(() => { });
    } else if (cdcaRascunho) {
      dispatch(updateId(cdcaRascunho.emissorId ?? ''));
      dispatch(updateCdcaCaracteristicas(CdcaParser.apiResponseToCaracteristicasState(cdcaRascunho, false)));
      dispatch(updateCriteriosRemuneracao(CdcaParser.apiResponseToCriteriosRemuneracaoState(cdcaRascunho)));
      dispatch(updateEventos(CdcaParser.apiResponseToEventosState(cdcaRascunho)));
      dispatch(updateDocuments(CdcaParser.apiResponseToCdcaDocumentsState(cdcaRascunho)));
    }
    defineTypeOfRegister();
  }, [cdcaApi, cdcaId, cdcaRascunho, dispatch]);

  const component = () => {
    switch (typeOfFormulario) {
      case TipoRegistroInstrumentoFinanceiro.Cadastro:
        return (
          <div>
            <FormularioCDCA
              cdcaId = {cdcaId}
              title={`Cadastro CDCA`}
              handleSubmit={() => reserveBeforeHandleSubmit(handleRegisterSubmit)}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={false}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Edicao:
        return (
          <div>
            <FormularioCDCA
              cdcaId = {cdcaId}
              title={`Editar CDCA`}
              handleSubmit={handleEditSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={true}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.EdicaoEmitido:
        return (
          <div>
            <FormularioCDCA
              cdcaId = {cdcaId}
              title={`Editar ${codigo}`}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={() => history.push('/instrumentos/gerenciar')}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
              codigo={codigo}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Aditamento:
        return (
          <div>
            <FormularioCDCA
              cdcaId = {cdcaId}
              title={`Editar ${codigo}`}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={true}
              codigo={codigo}
            />
          </div>
        );
      default:
        return <p>Loading</p>
    }
  };
  return component();
};

export default Cdca;
